import React from 'react';

export default function ExternalServicesExplanation() {
    const [selectedSection, setSelectedSection] = React.useState('definition');

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                    <h2 className="text-2xl font-bold mb-4">External Services</h2>
                    
                    <div className="mb-4 flex space-x-2">
                        {['definition', 'structure', 'examples', 'implementation'].map(section => (
                            <button
                                key={section}
                                onClick={() => setSelectedSection(section)}
                                className={`px-4 py-2 rounded ${
                                    selectedSection === section
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100'
                                }`}
                            >
                                {section.charAt(0).toUpperCase() + section.slice(1)}
                            </button>
                        ))}
                    </div>

                    {selectedSection === 'definition' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Services Externes</h3>
                            <ul className="space-y-2 list-disc list-inside text-gray-700">
                                <li>Intégration avec les systèmes externes (SWIFT, RTGS, ACH)</li>
                                <li>Gestion de la communication réseau</li>
                                <li>Gestion des erreurs et retries</li>
                                <li>Transformation des formats de données</li>
                                <li>Sécurité et authentification</li>
                            </ul>
                        </div>
                    )}

                    {selectedSection === 'structure' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Structure Type</h3>
                            <pre className="bg-gray-50 p-4 rounded text-sm">
{`// 1. Interface du Service (Port)
public interface ExternalBankingPort {
    TransactionResult processTransaction(
        TransactionRequest request
    );
    AccountInfo getAccountInfo(String iban);
    ValidationResult validateAccount(AccountRequest request);
}

// 2. Implémentation (Adapter)
@Service
@Slf4j
public class ExternalBankingService implements ExternalBankingPort {
    private final WebClient webClient;
    private final RetryTemplate retryTemplate;
    private final CircuitBreaker circuitBreaker;
    private final BankingMapper mapper;
    
    @Override
    public TransactionResult processTransaction(
        TransactionRequest request
    ) {
        return circuitBreaker.run(
            () -> executeWithRetry(() -> {
                // Préparation de la requête
                HttpRequest httpRequest = prepareRequest(request);
                
                // Exécution de la requête
                return webClient
                    .post()
                    .uri("/transactions")
                    .bodyValue(httpRequest)
                    .retrieve()
                    .bodyToMono(TransactionResponse.class)
                    .map(mapper::toResult)
                    .block();
            }),
            throwable -> handleFallback(throwable)
        );
    }
    
    private <T> T executeWithRetry(Supplier<T> operation) {
        return retryTemplate.execute(context -> {
            try {
                return operation.get();
            } catch (Exception e) {
                handleRetryError(e, context);
                throw e;
            }
        });
    }
    
    private void handleRetryError(Exception e, RetryContext context) {
        log.warn("Retry attempt {} failed: {}", 
            context.getRetryCount(), 
            e.getMessage());
    }
}`}
                            </pre>
                        </div>
                    )}

                    {selectedSection === 'examples' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Exemples de Services</h3>
                            
                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">SWIFT Client Service</h4>
                                <pre className="text-xs mt-2">
{`@Service
@Slf4j
public class SwiftClientService implements SwiftGatewayPort {
    private final SwiftClient swiftClient;
    private final SwiftMessageMapper mapper;
    private final CircuitBreaker circuitBreaker;
    
    @Override
    public SwiftResult sendMessage(SwiftMessage message) {
        return circuitBreaker.run(() -> {
            try {
                // Logging et monitoring
                log.info("Sending SWIFT message: {}", 
                    message.getReference());
                
                // Conversion du message
                String swiftMt = mapper.toMTFormat(message);
                
                // Envoi avec authentification
                SwiftResponse response = swiftClient
                    .withAuthentication(getAuthHeaders())
                    .sendMessage(swiftMt);
                
                // Traitement de la réponse
                handleResponse(response);
                
                return mapper.toResult(response);
                
            } catch (SwiftException e) {
                log.error("SWIFT error: {}", e.getMessage());
                throw new ExternalServiceException(
                    "SWIFT communication failed", e);
            }
        });
    }
    
    private Map<String, String> getAuthHeaders() {
        return Map.of(
            "Authorization", "Bearer " + getToken(),
            "MessageType", "MT103",
            "Priority", "NORMAL"
        );
    }
}`}
                                </pre>
                            </div>

                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">RTGS Gateway Service</h4>
                                <pre className="text-xs mt-2">
{`@Service
@Slf4j
public class RtgsGatewayService implements RtgsGatewayPort {
    private final RtgsClient rtgsClient;
    private final MessageMapper mapper;
    private final RetryTemplate retryTemplate;
    
    @Override
    public SettlementResult settlePayment(Payment payment) {
        MDC.put("paymentId", payment.getId().toString());
        log.info("Initiating RTGS settlement");
        
        try {
            return retryTemplate.execute(context -> {
                // Conversion en format RTGS
                RtgsMessage rtgsMsg = mapper.toRtgsFormat(payment);
                
                // Envoi sécurisé
                RtgsResponse response = rtgsClient
                    .withSecurityContext(buildSecurityContext())
                    .settle(rtgsMsg);
                
                // Vérification du statut
                if (!response.isSuccessful()) {
                    handleErrorResponse(response);
                }
                
                return mapper.toSettlementResult(response);
            });
            
        } catch (Exception e) {
            log.error("RTGS settlement failed", e);
            throw new SettlementException(
                "Failed to settle payment", e);
        } finally {
            MDC.clear();
        }
    }
    
    private SecurityContext buildSecurityContext() {
        return SecurityContext.builder()
            .certificateId(props.getCertificateId())
            .signature(calculateSignature())
            .timestamp(Instant.now())
            .build();
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}

                    {selectedSection === 'implementation' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Patterns d'Implémentation</h3>
                            
                            <div className="space-y-2">
                                <h4 className="font-medium">1. Circuit Breaker Pattern</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@Configuration
public class CircuitBreakerConfig {
    @Bean
    public CircuitBreaker externalServiceBreaker() {
        return CircuitBreaker.builder()
            .name("externalService")
            .failureRateThreshold(50)
            .waitDurationInOpenState(Duration.ofSeconds(60))
            .permittedNumberOfCallsInHalfOpenState(3)
            .slidingWindowSize(10)
            .build();
    }
}

@Service
public class ResilientService {
    private final CircuitBreaker circuitBreaker;
    
    public <T> T executeWithCircuitBreaker(
        Supplier<T> operation
    ) {
        return circuitBreaker.run(
            operation::get,
            this::handleFallback
        );
    }
}`}
                                </pre>
                            </div>

                            <div className="space-y-2">
                                <h4 className="font-medium">2. Retry Pattern</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@Configuration
public class RetryConfig {
    @Bean
    public RetryTemplate retryTemplate() {
        RetryTemplate template = new RetryTemplate();
        
        ExponentialBackOffPolicy backoff = 
            new ExponentialBackOffPolicy();
        backoff.setInitialInterval(1000);
        backoff.setMultiplier(2);
        backoff.setMaxInterval(30000);
        
        template.setBackOffPolicy(backoff);
        template.setRetryPolicy(new SimpleRetryPolicy(3));
        
        return template;
    }
}

@Service
public class RetryableService {
    private final RetryTemplate retry;
    
    public <T> T executeWithRetry(Supplier<T> operation) {
        return retry.execute(context -> {
            logRetryAttempt(context);
            return operation.get();
        });
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-lg p-4 shadow">
                    <h3 className="text-xl font-bold mb-4">Bonnes Pratiques</h3>
                    
                    <div className="space-y-4">
                        <div className="border-l-4 border-blue-500 pl-4">
                            <h4 className="font-semibold">1. Résilience</h4>
                            <p className="text-sm text-gray-600">
                                Implémentation de Circuit Breaker et Retry
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                CircuitBreaker, RetryTemplate
                            </code>
                        </div>

                        <div className="border-l-4 border-green-500 pl-4">
                            <h4 className="font-semibold">2. Sécurité</h4>
                            <p className="text-sm text-gray-600">
                                Gestion sécurisée des communications
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                SSL, Authentication, Encryption
                            </code>
                        </div>

                        <div className="border-l-4 border-purple-500 pl-4">
                            <h4 className="font-semibold">3. Monitoring</h4>
                            <p className="text-sm text-gray-600">
                                Traçage et métriques des appels externes
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                Logging, Metrics, Tracing
                            </code>
                        </div>

                        <div className="border-l-4 border-yellow-500 pl-4">
                            <h4 className="font-semibold">4. Error Handling</h4>
                            <p className="text-sm text-gray-600">
                                Gestion sophistiquée des erreurs
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                Custom Exceptions, Error Translation
                            </code>
                        </div>

                        <div className="border-l-4 border-red-500 pl-4">
                            <h4 className="font-semibold">5. Configuration</h4>
                            <p className="text-sm text-gray-600">
                                Configuration externalisée et flexible
                            </p>
                            <code className="text-xs bg-gray-100 p-1 mt-1 block">
                                Properties, Profiles, Env Variables
                            </code>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}